import React from 'react';
import { Drawer, Button, Space, Spin, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { GuideForm } from '@/features/guides/components/GuideForm';
import { type GuideFormData } from '@/features/guides/components/GuideForm';
import { type GuideContentRequest, type VehicleFilterRequest } from '@/features/guides/api/generated';
import { type VehicleSelectionValue } from '@/shared/components/VehicleEnrichment';

interface GenerateGuideDrawerProps {
  open: boolean;
  onClose: () => void;
  formState: GuideFormData;
  isDirty: boolean;
  isCreating: boolean;
  isGenerating: boolean;
  generateError: Error | null;
  createNewGuide: () => Promise<void>;
  updateContentField: (key: keyof GuideContentRequest, value: unknown) => void;
  updateRootVehicleWithSelection: (selection: VehicleSelectionValue) => void;
  addSolution: () => void;
  updateSolution: (index: number, value: string) => void;
  removeSolution: (index: number) => void;
  reorderSolutions: (fromIndex: number, toIndex: number) => void;
  addFilter: (filter: VehicleFilterRequest) => void;
  removeFilterAt: (index: number) => void;
  updateFilterAt: (index: number, filter: VehicleFilterRequest) => void;
}

export const GenerateGuideDrawer: React.FC<GenerateGuideDrawerProps> = ({
  open,
  onClose,
  formState,
  isDirty,
  isCreating,
  isGenerating,
  generateError,
  createNewGuide,
  updateContentField,
  updateRootVehicleWithSelection,
  addSolution,
  updateSolution,
  removeSolution,
  reorderSolutions,
  addFilter,
  removeFilterAt,
  updateFilterAt,
}) => {
  const { t } = useTranslation();

  return (
    <Drawer
      title={t('clusters.generateDrawer.title')}
      placement="right"
      width={600}
      open={open}
      onClose={onClose}
      footer={
        !isGenerating && !generateError ? (
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={onClose}>{t('common.cancel')}</Button>
              <Button
                type="primary"
                loading={isCreating}
                onClick={createNewGuide}
                disabled={!isDirty || isCreating}
              >
                {isCreating ? t('clusters.generateDrawer.savingGuide') : t('clusters.generateDrawer.saveGuide')}
              </Button>
            </Space>
          </div>
        ) : undefined
      }
    >
      {isGenerating && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, fontSize: 16, fontWeight: 500 }}>
            {t('clusters.generateDrawer.generating')}
          </div>
          <div style={{ marginTop: 8, color: '#8c8c8c' }}>
            {t('clusters.generateDrawer.generatingDescription')}
          </div>
        </div>
      )}

      {generateError && (
        <Alert
          message={t('clusters.results.generateError')}
          description={t('clusters.results.generateErrorDescription')}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {!isGenerating && !generateError && (
        <>
          <div style={{ marginBottom: 16, color: '#8c8c8c' }}>
            {t('clusters.generateDrawer.subtitle')}
          </div>
          <GuideForm
            formData={formState}
            onUpdateContent={updateContentField}
            onUpdateRootVehicle={updateRootVehicleWithSelection}
            onAddSolution={addSolution}
            onUpdateSolution={updateSolution}
            onRemoveSolution={removeSolution}
            onReorderSolutions={reorderSolutions}
            onAddFilter={addFilter}
            onRemoveFilter={removeFilterAt}
            onUpdateFilter={updateFilterAt}
            mode="dashboard"
          />
        </>
      )}
    </Drawer>
  );
};
